import BasicButton from "@/components/core/buttons/BasicButton";
import Loader from "@/components/core/layouts/Loader";
import {
  requestGetTransactionDetail,
  requestPostExtendTransaction,
} from "@/state/transactions/TransactionEffects";
import { PhoneHelper } from "@/utils/helpers/phone-helper";
import { toastError, toastSuccess } from "@/utils/helpers/toast-helper";
import EditIcon from "@static/core/icons/icon-edit.svg?react";
import { Document, Signatory } from "@type/core/dashboard/dashboard.types";
import { TransactionDetail } from "@type/core/transaction/transaction.types";
import { SIGNATORY_STATUS_ENUM } from "@utils/Constant";
import { Input, Space } from "antd";
import { ColumnsType } from "antd/lib/table/interface";
import i18n from "i18next";
import { Dispatch, SetStateAction } from "react";
import DetailSignatory from "@views/core/extranet/transactions/details/DetailSignatory";
import RelaunchDetailSignatoryComponent from "@views/core/extranet/transactions/details/columns/RelaunchDetailSignatoryComponent";
import { ActiveUser } from "@/type/core/auth/auth.types";
import { Right } from "@/type/core/offices/offices.types";

interface TransactionSignatoriesColumnsInterface {
  isDraggable: boolean;
  transaction: TransactionDetail;
  documents: Document[];
  authorized: boolean;
  editMode: string | undefined;
  setEditMode: Dispatch<SetStateAction<string>>;
  activateSignLevel2?: boolean;
  updateSignatory: () => void;
  isLoading: boolean;
  temporaryEditableSignatory?: Signatory;
  setTemporaryEditableSignatory?: Dispatch<
    SetStateAction<Signatory | undefined>
  >;
  directSignature?: boolean;
  isOrderChanged?: boolean;
  signatureDegradedMode: boolean;
  isOtherSignatoryInProgress?: boolean;
  setIsLoading: (isLoading: boolean) => void;
  user: ActiveUser | undefined;
  officeRights: Right[];
  hasMultipleNextSimultaneousSignatories: boolean;
}

export const columns = ({
  isDraggable,
  transaction,
  documents,
  authorized,
  editMode,
  setEditMode,
  activateSignLevel2 = false,
  updateSignatory,
  isLoading,
  temporaryEditableSignatory,
  setTemporaryEditableSignatory,
  directSignature,
  isOrderChanged = false,
  signatureDegradedMode,
  isOtherSignatoryInProgress,
  setIsLoading,
  user,
  officeRights,
  hasMultipleNextSimultaneousSignatories,
}: TransactionSignatoriesColumnsInterface): ColumnsType<Signatory> => {
  const renderLoaderOrContent = (
    content: React.ReactNode,
    signatoryId?: string,
  ) => {
    // If isLoading is true and signatory.id matches temporaryEditableSignatory.id, show the Loader
    if (
      isLoading &&
      (temporaryEditableSignatory === undefined ||
        temporaryEditableSignatory?.id === signatoryId)
    ) {
      return <Loader className="m-3 text-center" />;
    }
    // Otherwise, return the content (normal display of columns)
    return content;
  };

  async function handleRelaunchSignatories() {
    try {
      const response = await requestPostExtendTransaction({
        id: transaction.id,
      });

      if (response.ok) {
        toastSuccess(i18n.t("success.transactionDetail.signatoriesRelaunch"));

        try {
          const detailResponse = await requestGetTransactionDetail({
            id: transaction.id,
          });

          if (!detailResponse.ok) {
            toastError(i18n.t("error.transactionDetail.retrieve"));
          }
        } catch (error) {
          console.error(error);
          toastError(i18n.t("error.transactionDetail.retrieve"));
        }
      } else {
        toastError(i18n.t("error.transactionDetail.signatoriesRelaunch"));
      }
    } catch (error) {
      console.error(error);
      toastError(i18n.t("error.transactionDetail.signatoriesRelaunch"));
    } finally {
      setIsLoading(false);
    }
  }

  return [
    ...(isDraggable
      ? [
          {
            key: "sort",
            width: 38,
            className: "text-center",
          },
        ]
      : []),
    {
      dataIndex: "firstname",
      render: (firstname: string, signatory: Signatory) => {
        const key = signatory.key ?? signatory.id;
        const handleInputFirstnameChange = (
          e: React.ChangeEvent<HTMLInputElement>,
        ) => {
          if (temporaryEditableSignatory && setTemporaryEditableSignatory) {
            setTemporaryEditableSignatory({
              ...temporaryEditableSignatory,
              firstname: e.target.value,
            });
          }
        };

        const handleInputLastnameChange = (
          e: React.ChangeEvent<HTMLInputElement>,
        ) => {
          if (temporaryEditableSignatory && setTemporaryEditableSignatory) {
            setTemporaryEditableSignatory({
              ...temporaryEditableSignatory,
              lastname: e.target.value,
            });
          }
        };
        return renderLoaderOrContent(
          <div
            title={firstname + " " + signatory.lastname}
            className={
              signatory.status === SIGNATORY_STATUS_ENUM.ERROR[0]
                ? "text-danger"
                : ""
            }
          >
            {editMode === key ? (
              <Space.Compact>
                <Input
                  value={temporaryEditableSignatory?.firstname ?? firstname}
                  onChange={handleInputFirstnameChange}
                  maxLength={45}
                />
                <Input
                  value={
                    temporaryEditableSignatory?.lastname ?? signatory.lastname
                  }
                  onChange={handleInputLastnameChange}
                  maxLength={45}
                />
              </Space.Compact>
            ) : (
              <td>{firstname + " " + signatory.lastname}</td>
            )}
          </div>,
          signatory.id,
        );
      },
    },
    {
      dataIndex: "email",
      render: (email: string, { id, status, key }: Signatory) => {
        key = key ?? id;

        const handleInputEmailChange = (
          e: React.ChangeEvent<HTMLInputElement>,
        ) => {
          if (temporaryEditableSignatory && setTemporaryEditableSignatory) {
            setTemporaryEditableSignatory({
              ...temporaryEditableSignatory,
              email: e.target.value,
            });
          }
        };

        return renderLoaderOrContent(
          <div
            title={email}
            className={
              status === SIGNATORY_STATUS_ENUM.ERROR[0] ? "text-danger" : ""
            }
          >
            {editMode === key ? (
              <Input
                value={temporaryEditableSignatory?.email ?? email}
                onChange={handleInputEmailChange}
                maxLength={255}
                type="email"
              />
            ) : (
              <td>{email}</td>
            )}
          </div>,
          id,
        );
      },
    },
    {
      dataIndex: "phone",
      render: (phone: string, { id, status, key }: Signatory) => {
        key = key ?? id;

        const handleInputPhoneChange = (
          e: React.ChangeEvent<HTMLInputElement>,
        ) => {
          if (temporaryEditableSignatory && setTemporaryEditableSignatory) {
            setTemporaryEditableSignatory({
              ...temporaryEditableSignatory,
              phone: e.target.value,
            });
          }
        };

        return renderLoaderOrContent(
          <div
            title={phone ? PhoneHelper.formatPhone(phone) : "-"}
            className={
              status === SIGNATORY_STATUS_ENUM.ERROR[0] ? "text-danger" : ""
            }
          >
            {editMode === key ? (
              <Input
                value={temporaryEditableSignatory?.phone ?? phone}
                onChange={handleInputPhoneChange}
                maxLength={12}
                type="tel"
              />
            ) : phone ? (
              PhoneHelper.formatPhone(phone)
            ) : (
              <td>{"-"}</td>
            )}
          </div>,
          id,
        );
      },
    },
    {
      dataIndex: "key",
      width: 44,
      render: (key: string, signatory: Signatory) => {
        key = key ?? signatory.id;
        return renderLoaderOrContent(
          <div>
            {signatory.isEditable && editMode !== key ? (
              <EditIcon
                className="picto icon-30"
                onClick={() => {
                  setTemporaryEditableSignatory?.(signatory);
                  setEditMode(key);
                }}
              />
            ) : (
              <></>
            )}
          </div>,
          signatory.id,
        );
      },
    },
    {
      title: (
        <span className="d-block">
          {i18n.t("signature.signatories.withCertificate")}
        </span>
      ),
      dataIndex: "email",
      render: (_email: string, signatory: Signatory) =>
        renderLoaderOrContent(
          <DetailSignatory
            signatory={signatory}
            documents={documents}
            activateSignLevel2={activateSignLevel2}
          />,
          signatory.id,
        ),
    },
    {
      title: () => {
        if (
          transaction.status === "inProgress" &&
          transaction.invitationMode === "parallel" &&
          transaction.notification != "DISABLED" &&
          hasMultipleNextSimultaneousSignatories
        ) {
          return (
            <div className={"d-flex justify-content-end"}>
              <BasicButton
                variant="primary"
                text={i18n.t("transaction.detail.signatoryRelaunchAll")}
                disabled={signatureDegradedMode}
                onClick={handleRelaunchSignatories}
              />
            </div>
          );
        }
        return null;
      },
      dataIndex: "key",
      className: "text-center",
      render: (key: string, signatory: Signatory) => (
        <RelaunchDetailSignatoryComponent
          keyId={key}
          signatory={signatory}
          transaction={transaction}
          authorized={authorized}
          editMode={editMode}
          setEditMode={setEditMode}
          updateSignatory={updateSignatory}
          setTemporaryEditableSignatory={setTemporaryEditableSignatory}
          directSignature={directSignature}
          isOrderChanged={isOrderChanged}
          signatureDegradedMode={signatureDegradedMode}
          isOtherSignatoryInProgress={isOtherSignatoryInProgress}
          setIsLoading={setIsLoading}
          user={user}
          officeRights={officeRights}
        />
      ),
    },
  ];
};
